<script lang="ts">
  import { getAttachment } from "../gmail/api";
  import { base64UrlToBytes, type MessageAttachment } from "../gmail/parser";

  interface Props {
    messageId: string;
    attachments: MessageAttachment[];
  }

  let { messageId, attachments }: Props = $props();

  let downloading = $state<string | null>(null);
  let error = $state<string | null>(null);

  function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  function fileExt(filename: string): string {
    const m = filename.match(/\.([^.]+)$/);
    return m ? m[1].toUpperCase().slice(0, 4) : "FILE";
  }

  /** Couleur déterministe par extension — petite touche visuelle. */
  function extColor(ext: string): string {
    const map: Record<string, string> = {
      PDF: "var(--bad)",
      JPG: "var(--accent)", JPEG: "var(--accent)", PNG: "var(--accent)", GIF: "var(--accent)", WEBP: "var(--accent)", HEIC: "var(--accent)",
      DOC: "var(--accent)", DOCX: "var(--accent)",
      XLS: "var(--accent)", XLSX: "var(--accent)", CSV: "var(--accent)",
      PPT: "var(--accent)", PPTX: "var(--accent)",
      ZIP: "var(--accent)", RAR: "var(--accent)", "7Z": "var(--accent)",
      MP3: "var(--accent)", WAV: "var(--accent)", M4A: "var(--accent)",
      MP4: "var(--accent)", MOV: "var(--accent)", AVI: "var(--accent)",
    };
    return map[ext] ?? "var(--fg-subtle)";
  }

  async function open(att: MessageAttachment) {
    if (downloading) return;
    downloading = att.attachmentId;
    error = null;
    try {
      const data = await getAttachment(messageId, att.attachmentId);
      const bytes = base64UrlToBytes(data.data);
      const blob = new Blob([bytes], { type: att.mimeType });
      const url = URL.createObjectURL(blob);

      // iOS Safari (PWA standalone) bloque `window.open()` qu'on appelle
      // après un `await` (= plus user-initiated). On déclenche un click
      // synthétique sur un `<a download>` à la place — iOS ouvre alors
      // son sheet natif "Save to Files / Open in" qui propose à la fois
      // le preview (PDF, image, etc.) et la sauvegarde.
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      a.rel = "noopener";
      // target=_blank : Chrome/Firefox ouvre en preview si format connu,
      // sinon download. iOS ignore et fait le sheet natif.
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Revoke après délai pour laisser le navigateur consommer le blob
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      downloading = null;
    }
  }
</script>

{#if attachments.length > 0}
  <div class="attachments">
    {#each attachments as att (att.attachmentId)}
      {@const ext = fileExt(att.filename)}
      <button
        class="att"
        onclick={() => open(att)}
        disabled={downloading === att.attachmentId}
        aria-label="Open {att.filename}"
      >
        <div class="ext-badge" style="background: {extColor(ext)}">{ext}</div>
        <div class="info">
          <div class="name">{att.filename}</div>
          <div class="size">{formatSize(att.size)}</div>
        </div>
        {#if downloading === att.attachmentId}
          <div class="spinner" aria-hidden="true"></div>
        {/if}
      </button>
    {/each}
    {#if error}
      <div class="err">⚠ {error}</div>
    {/if}
  </div>
{/if}

<style>
  .attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 12px 0;
  }

  .att {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px 8px 8px;
    background: var(--bg-hover);
    border: 0.5px solid var(--border-subtle);
    border-radius: 10px;
    cursor: pointer;
    font: inherit;
    font-family: var(--font-sans);
    color: inherit;
    text-align: left;
    max-width: 100%;
    transition: background 0.1s;
    -webkit-tap-highlight-color: transparent;
  }
  .att:hover { background: color-mix(in srgb, var(--fg) 5%, var(--bg-hover)); }
  .att:active { transform: scale(0.98); }
  .att:disabled { opacity: 0.6; cursor: wait; }

  .ext-badge {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    display: grid;
    place-items: center;
    color: white;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    flex-shrink: 0;
    user-select: none;
  }

  .info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 2px;
  }
  .name {
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 500;
    color: var(--fg);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
  }
  .size {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--fg-subtle);
  }

  .spinner {
    width: 12px;
    height: 12px;
    border: 2px solid var(--border);
    border-top-color: var(--fg);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .err {
    padding: 8px 10px;
    background: var(--accent-soft);
    color: var(--accent);
    border-radius: 8px;
    font-size: 12px;
    width: 100%;
  }
</style>
